import { useCallback } from 'react';
import ImageResizer from 'utils/packages/imageResizer';
import ReactNativeBlobUtil from 'utils/packages/bloblUtil';
import Amplitude from 'utils/amplitude';
import { useAppSelector } from 'store/hooks';
import { ThrowableFetchError, muteAll, useFetchOld } from 'hooks';
import { selectIsChangeOfAddress } from 'reducers/selectors';
import * as actions from '../actions';
import { KYC_MAX_UPLOAD_MB } from '../constants';
import { getOnboardingFlow } from '../utils/analytics';
export async function resizeFile(file, sizeLimit, scaleDownFactor) {
    log(file);
    const { size } = await ReactNativeBlobUtil.fs.stat(file.path.replace('file://', ''));
    if (size > sizeLimit) {
        log(`Reducing image. Original size: ${Math.round(size / 1024)} KB, limit: ${Math.round(sizeLimit / 1024)} KB`, false, 'yellow');
        const coefficient = scaleDownFactor ?? Math.min(0.9, 1 / Math.sqrt(size / (sizeLimit * 0.9))); // using 90% of the limit, as a safety measure, and additionally ensuring that we are sizing it down, not scaling up
        const result = await ImageResizer.createResizedImage(file.path, Math.round(coefficient * file.width), Math.round(coefficient * file.height), 'JPEG', 100);
        return resizeFile(result, sizeLimit, 0.8); // ensure file size matches the condition after resizing, and if not - scale image down to 80% each side
    }
    log(`The image was good, size: ${Math.round(size)} KB`, false, 'green');
    return file;
}
function resizeFiles(files, sizeLimit) {
    return Promise.all(files.map((file) => resizeFile(file, sizeLimit)));
}
const fetchConfig = {
    throws: true,
    ...muteAll,
};
const useDocumentUpload = (flow) => {
    const isChangeOfAddress = useAppSelector(selectIsChangeOfAddress);
    const [, , , fetch] = useFetchOld(fetchConfig);
    const upload = useCallback(async (param) => {
        try {
            const response = await fetch(actions.getDocumentUploadUrl(param.type, param.files.length, param.addressId, param.addressId ? isChangeOfAddress : undefined));
            if (response) {
                const { documentUploadUrls, selfieDocumentUploadUrl } = response;
                const { files } = param;
                let finalFiles = [...files];
                if (KYC_MAX_UPLOAD_MB) {
                    finalFiles = await resizeFiles(finalFiles, KYC_MAX_UPLOAD_MB * 1024 * 1024);
                }
                // Upload images, they all have small size now (under KYC_MAX_UPLOAD_MB)
                const uploadPromises = [];
                for (let i = 0; i < documentUploadUrls.length; i += 1) {
                    const mime = param.mimeType || 'image/jpeg';
                    const promise = ReactNativeBlobUtil.fetch('PUT', documentUploadUrls[i], { 'Content-Type': mime }, ReactNativeBlobUtil.wrap(finalFiles[i].path.replace('file://', '')));
                    uploadPromises.push(promise);
                }
                if (selfieDocumentUploadUrl) {
                    if (!param.selfieFile) {
                        throw new Error('Needs selfie');
                    }
                    const mime = param.mimeType || 'image/jpeg';
                    const promise = ReactNativeBlobUtil.fetch('PUT', selfieDocumentUploadUrl, { 'Content-Type': mime }, ReactNativeBlobUtil.wrap(param.selfieFile.path.replace('file://', '')));
                    uploadPromises.push(promise);
                }
                await Promise.all(uploadPromises);
                Amplitude.logEvent('Uploaded Document', {
                    source: getOnboardingFlow(flow),
                    type: param.type === 'ADDRESS' ? 'POA' : 'POI',
                });
            }
        }
        catch (e) {
            if (e.message === 'Needs selfie') {
                throw e;
            }
            const fetchError = e instanceof ThrowableFetchError ? e : undefined;
            const errorToShow = fetchError
                ? fetchError.fetchError.errorMessage ?? ''
                : 'This is awkward, please try again.';
            throw new Error(errorToShow);
        }
    }, [flow, isChangeOfAddress]);
    return [upload];
};
export default useDocumentUpload;
